import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { RE_PLEX_ID } from '../lib/utils';

const BATCH_DEBOUNCE_MS = 100;

class SourceReferencesStore {
  sources = [];
  highlightedSourceIds = [];  // supports multi-source highlight
  scrollToSourceId = null;
  isDrawerOpen = false;
  urlToSourceMap = new Map(); // For O(1) deduplication
  nextSourceId = 1;
  metadataMap = new Map(); // sourceId → {loading, title, description, favicon_url, image_url, domain, error}
  accessInstructionsModal = null; // {href, instructions, datasetName} or null
  _pendingBatch = [];  // [{sourceId, url}]
  _batchTimer = null;
  _batchEndpoint = null;

  constructor() {
    makeObservable(this, {
      sources: observable.deep,
      highlightedSourceIds: observable,
      scrollToSourceId: observable,
      isDrawerOpen: observable,
      accessInstructionsModal: observable,
      dedupedSources: computed,
      highlightedSourceId: computed,
      addSource: action,
      highlightSource: action,
      scrollToSource: action,
      clearHighlight: action,
      toggleDrawer: action,
      closeDrawer: action,
      clearSources: action,
      removeSourcesByAgentId: action,
      loadDrawerState: action,
      metadataMap: observable,
      fetchMetadata: action,
      showAccessInstructions: action,
      hideAccessInstructions: action,
    });
  }

  // Backwards-compatible single-ID accessor
  get highlightedSourceId() {
    return this.highlightedSourceIds[0] ?? null;
  }

  get dedupedSources() {
    return this.sources;
  }

  addSource(url, title, turnId, agentId = null) {
    // Validate URL
    if (!url || url.trim() === '') {
      return null;
    }

    // Plex IDs should NOT be stored - they should be resolved to HTTP URLs first
    if (RE_PLEX_ID.test(url)) {
      // Plex IDs should be resolved to HTTP URLs before registration
      // If we see one here, it means interpolateSourceLinks hasn't run yet
      return null;
    }

    // Enhanced URL validation - check for basic URL format
    const isValidUrl = url.includes('://');

    // Check for existing source
    if (this.urlToSourceMap.has(url)) {
      const sourceId = this.urlToSourceMap.get(url);
      const existingSource = this.sources.find(s => s.id === sourceId);
      if (existingSource) {
        const isDupe = existingSource.occurrences.some(
          occ => occ.turnId === turnId && occ.agentId === agentId
        );
        if (!isDupe) existingSource.occurrences.push({turnId, agentId});
        if (title && (!existingSource.title || title.length > existingSource.title.length)) {
          existingSource.title = title;
        }
        return existingSource.id;
      }
    }

    // Create new source
    const sourceId = `source-${this.nextSourceId++}`;
    const source = {
      id: sourceId,
      url,
      title,
      turnId,
      agentId,
      firstSeenAt: Date.now(),
      occurrences: [{turnId, agentId}],
      isInvalid: !isValidUrl,
    };

    this.sources.push(source);
    this.urlToSourceMap.set(url, sourceId);

    return sourceId;
  }
  highlightSource(ids) {
    this.highlightedSourceIds = Array.isArray(ids) ? ids : (ids ? [ids] : []);
  }

  scrollToSource(id) {
    this.highlightedSourceIds = id ? [id] : [];
    this.scrollToSourceId = id;
    // Reset after one tick so repeated clicks on the same card re-trigger the effect
    setTimeout(() => {
      runInAction(() => { this.scrollToSourceId = null; });
    }, 0);
  }

  clearHighlight() {
    this.highlightedSourceIds = [];
  }

  toggleDrawer() {
    this.isDrawerOpen = !this.isDrawerOpen;

    // Persist state to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('sourceDrawerOpen', JSON.stringify(this.isDrawerOpen));
    }
  }

  closeDrawer() {
    this.isDrawerOpen = false;

    // Persist state to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('sourceDrawerOpen', JSON.stringify(this.isDrawerOpen));
    }
  }

  clearSources() {
    this.sources = [];
    this.urlToSourceMap.clear();
    // Clear the per-source metadata cache too. nextSourceId resets to 1 below,
    // so the next chat reuses ids (source-1, source-2, ...). Without clearing
    // metadataMap, each SourceCard would read the PREVIOUS chat's cached title
    // and structure image under the reused id — rendering another chat's
    // source (e.g. a "Sotorasib" compound image) in this chat.
    this.metadataMap.clear();
    this.highlightedSourceIds = [];
    this.nextSourceId = 1;
  }

  removeSourcesByAgentId(agentId) {
    if (!agentId) return;

    // Remove occurrences that match this agentId
    this.sources.forEach(source => {
      source.occurrences = source.occurrences.filter(
        occ => occ.agentId !== agentId
      );
    });

    // Remove sources that have no remaining occurrences
    const sourcesToRemove = this.sources.filter(s => s.occurrences.length === 0);
    sourcesToRemove.forEach(source => {
      this.urlToSourceMap.delete(source.url);
    });
    this.sources = this.sources.filter(s => s.occurrences.length > 0);
  }

  getSource(id) {
    return this.sources.find(s => s.id === id) || null;
  }

  fetchMetadata(sourceId, url, metadataEndpoint, authData = null) {
    const existing = this.metadataMap.get(sourceId);
    if (existing && (existing.loading || existing.title || existing.error)) return;

    this.metadataMap.set(sourceId, { loading: true });

    this._batchEndpoint = metadataEndpoint;
    if (authData) this._batchAuthData = authData;
    this._pendingBatch.push({ sourceId, url });

    if (this._batchTimer) clearTimeout(this._batchTimer);
    this._batchTimer = setTimeout(() => this._flushBatch(), BATCH_DEBOUNCE_MS);
  }

  _flushBatch() {
    const batch = this._pendingBatch.splice(0);
    const endpoint = this._batchEndpoint;
    const authData = this._batchAuthData;
    this._batchTimer = null;

    if (batch.length === 0) return;

    const urls = [...new Set(batch.map(b => b.url))];
    const sourcesByUrl = new Map();
    for (const { sourceId, url } of batch) {
      if (!sourcesByUrl.has(url)) sourcesByUrl.set(url, []);
      sourcesByUrl.get(url).push(sourceId);
    }

    const headers = { 'Content-Type': 'application/json' };
    const jwtToken = authData?.jwtToken || authData?.idToken?.jwtToken;
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;

    fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ urls }),
    })
      .then(resp => resp.json())
      .then(data => {
        runInAction(() => {
          const results = data.results || {};
          for (const [url, sourceIds] of sourcesByUrl) {
            const meta = results[url] || { error: true };
            for (const sid of sourceIds) {
              this.metadataMap.set(sid, { ...meta, loading: false });
            }
          }
        });
      })
      .catch(() => {
        runInAction(() => {
          for (const { sourceId } of batch) {
            this.metadataMap.set(sourceId, { loading: false, error: true });
          }
        });
      });
  }

  showAccessInstructions(href, instructions, datasetName) {
    this.accessInstructionsModal = { href, instructions, datasetName };
  }

  hideAccessInstructions() {
    this.accessInstructionsModal = null;
  }

  // Load drawer state from localStorage on init
  loadDrawerState() {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('sourceDrawerOpen');
      if (saved !== null) {
        this.isDrawerOpen = JSON.parse(saved);
      }
    }
  }
}

export default SourceReferencesStore;